/**
 * Iteration D (AC-1101..1103): pure interpretation layer — turns experiment
 * evidence (emulation proposal feed / A/B decision verdict) into the
 * operator-facing brief → detail → action-items structure.
 *
 * Display-only by contract: nothing here mutates state; the card renders the
 * result. All copy is plain English inline (no i18n), same as the Reports
 * warning sections.
 *
 * FAMILY_GUIDE covers every proposal family the U5 compiler can emit
 * (simulation-harness/proposalInputs.ts PROPOSAL_FAMILY values) — the unit
 * suite locks the two vocabularies together, so a new estimator family
 * without a guide entry fails the tests instead of rendering raw ids.
 */

import type { DecisionVerdictResult } from './decisionVerdict';
import type {
  ExperimentSurfaceStatus,
  ExperimentTrustAssessment,
  ProposalFeedRow,
  ReviewSummaryBlock,
} from './experimentSurface';
import type { ExperimentFeedIdentity } from './experimentTypes';

/** What the operator is asked to do (AC-1103 kind vocabulary). */
export type InterpretationActionKind =
  | 'edit_options'
  | 'fork_version'
  | 'revisit_insights'
  | 'adjust_audience'
  | 'fix_measurement'
  | 'collect_evidence'
  | 'declare_winner'
  | 'rollout';

export interface InterpretationAction {
  kind: InterpretationActionKind;
  /** Full imperative sentence naming its target (AC-1103). */
  text: string;
  /** Where in the product to do it — rendered as a secondary caption. */
  where?: string;
  targetEntity?: string;
  targetId?: string;
  targetName?: string;
  /** Lineage refs for findability deep links (AC-1106). */
  analysisId?: string;
  insightIds?: string[];
  audienceId?: string;
}

export interface ExperimentInterpretation {
  /** ONE sentence for the collapsed card: top action or collecting/blocked state. */
  headline: string;
  /** "What this means" lines for the expanded card. */
  details: string[];
  /** Numbered, concrete action items. */
  actions: InterpretationAction[];
  /**
   * Spec D P6 stop valve (§7.2.3): true when the SRM check failed — the split
   * is corrupted, so cards must suppress candidate/follow-up actions (only the
   * fix_measurement action remains) until measurement is repaired.
   */
  stopValve?: boolean;
}

type Direction = 'up' | 'down' | 'inconclusive';

export interface FamilyGuideEntry {
  /** Human name of the family (never the raw snake_case id). */
  label: string;
  /** What this family of evidence measures. */
  measures: string;
  /** Plain-language meaning per estimated direction; {target} is substituted. */
  meaning: Record<Direction, string>;
  /** What to do about it, per direction; {target} is substituted. */
  action: Record<Direction, string>;
  actionKind: InterpretationActionKind;
  /** Where in the product the operator finds the target. */
  whereToLook: string;
}

/**
 * One entry per PROPOSAL_FAMILY value (estimator doc §6 mapping). Direction is
 * the sign of the posterior interval on the hidden state: `up` = the real
 * signal is stronger than the modeled assumption, `down` = weaker/contradicts.
 */
export const FAMILY_GUIDE: Record<string, FamilyGuideEntry> = {
  audience_refine: {
    label: 'Audience mix',
    measures: 'How much of the real converting traffic each modeled audience actually explains.',
    meaning: {
      up: 'Audience {target} explains more real journeys than its current weight assumes.',
      down: 'Audience {target} explains fewer real journeys than assumed — its weight overstates it.',
      inconclusive: 'The evidence on audience {target} points in no clear direction yet.',
    },
    action: {
      up: 'Increase the weight of audience {target} in the project audience mix.',
      down: 'Reduce the weight of audience {target}, or regenerate it from fresher insights.',
      inconclusive: 'Leave audience {target} unchanged until the next run gives a clearer read.',
    },
    actionKind: 'adjust_audience',
    whereToLook: 'Product page → Audiences; the linked insight analysis shows where this audience came from.',
  },
  actor_adjust_representativeness: {
    label: 'Actor representativeness',
    measures: 'Whether this synthetic actor answers and navigates like the real users it stands for.',
    meaning: {
      up: 'Actor {target} matches real journeys well — simulations that lean on it are trustworthy.',
      down: 'Actor {target} answers unlike real users — it misleads simulations that include it.',
      inconclusive: 'Not enough matched journeys to say whether actor {target} represents real users.',
    },
    action: {
      up: 'Keep actor {target}; consider raising its weight in simulation runs.',
      down: 'Reduce the weight of actor {target} or regenerate it from the insight analysis it came from.',
      inconclusive: 'Keep actor {target} as is; collect more journeys before adjusting it.',
    },
    actionKind: 'adjust_audience',
    whereToLook: 'Product page → Audiences → the actor’s audience; regenerate from the analysis named on this row.',
  },
  actor_adjust_value_signal: {
    label: 'Actor value signal',
    measures: 'Whether this actor’s modeled willingness to pay matches real purchase behavior.',
    meaning: {
      up: 'Real buyers convert more than actor {target} predicts — its value signal is understated.',
      down: 'Real purchase behavior is weaker than actor {target} predicts — its value signal is inflated.',
      inconclusive: 'Purchases are too sparse to grade the value signal of actor {target}.',
    },
    action: {
      up: 'Raise the purchase-intent weight of actor {target} to match observed buying.',
      down: 'Lower the purchase-intent weight of actor {target}, or regenerate it against real buyer data.',
      inconclusive: 'Keep the value weights of actor {target}; wait for more purchase evidence.',
    },
    actionKind: 'adjust_audience',
    whereToLook: 'Product page → Audiences → the actor’s audience; purchase evidence lives in Reports.',
  },
  insight_promote_demote: {
    label: 'Insight relevance',
    measures: 'Whether the insight behind this content still matches how real users behave.',
    meaning: {
      up: 'Real journeys confirm insight {target} — content built on it resonates.',
      down: 'Real behavior contradicts insight {target} — content leaning on it underperforms.',
      inconclusive: 'The journeys touching insight {target} are too mixed to grade it.',
    },
    action: {
      up: 'Promote insight {target}: give it more weight in the next strategy generation.',
      down: 'Demote insight {target} in its analysis and regenerate the content that leans on it.',
      inconclusive: 'Keep insight {target} as is; the next run window may separate it.',
    },
    actionKind: 'revisit_insights',
    whereToLook: 'Product page → Insights → the analysis named on this row.',
  },
  variable_option_action: {
    label: 'Question options',
    measures: 'Whether the answer options of this onboarding question produce a clean signal about who the user is.',
    meaning: {
      up: 'Options on {target} separate audiences well — the question is pulling its weight.',
      down: 'Some options on {target} attract noise or almost no picks — they blur who the user is.',
      inconclusive: 'The signal from {target} is mixed — no clear read on its options yet.',
    },
    action: {
      up: 'Keep {target} as is; consider adding more specific options to sharpen it further.',
      down: 'Edit {target}: remove or relabel the weak options (the run review names the exact edits).',
      inconclusive: 'Leave {target} unchanged until the next run gives a clearer read.',
    },
    actionKind: 'edit_options',
    whereToLook: 'Onboarding editor → the screen asking this question; the run bundle names the exact options.',
  },
  artifact_action: {
    label: 'Funnel version effect',
    measures: 'Whether this published funnel version itself helps or hurts outcomes, beyond who arrives.',
    meaning: {
      up: 'Version {target} moves outcomes up — its structure and copy work.',
      down: 'Version {target} drags outcomes down — something in its structure or copy hurts.',
      inconclusive: 'The effect of version {target} cannot be separated from traffic mix yet.',
    },
    action: {
      up: 'Keep {target} live and bank it as the baseline for the next iteration.',
      down: 'Fork {target} into a corrected version and verify the fix with an A/B test.',
      inconclusive: 'Keep {target} live; rerun after the traffic mix stabilizes.',
    },
    actionKind: 'fork_version',
    whereToLook: 'Onboardings → versions; the candidate patch in the run bundle lists the planned changes.',
  },
  measurement_scope_limit: {
    label: 'Traffic context',
    measures: 'Whether the traffic mix (source, device, campaign) skews what this window can prove.',
    meaning: {
      up: 'The traffic context for {target} is stable — conclusions generalize across the observed sources.',
      down: 'The traffic mix behind {target} is skewed — conclusions only hold for the observed sources.',
      inconclusive: 'Traffic context for {target} is too thin to grade.',
    },
    action: {
      up: 'No change needed; the traffic mix supports the conclusions in this window.',
      down: 'Limit conclusions for {target} to the observed traffic mix; segment or rebalance sources before generalizing.',
      inconclusive: 'Compare per-source numbers in Reports before generalizing from {target}.',
    },
    actionKind: 'fix_measurement',
    whereToLook: 'Reports → traffic filters; compare sources before trusting cross-source conclusions.',
  },
  measurement_data_action: {
    label: 'Measurement reliability',
    measures: 'Whether tracking captured journeys completely enough to trust the numbers at all.',
    meaning: {
      up: 'Tracking for {target} is capturing journeys completely — the numbers are trustworthy.',
      down: 'Tracking for {target} is losing events or identities — every estimate downstream inherits that gap.',
      inconclusive: 'Not enough telemetry to grade measurement health for {target}.',
    },
    action: {
      up: 'No change needed; measurement is healthy for {target}.',
      down: 'Fix instrumentation for {target} (lost events / identity capture) before acting on any estimate.',
      inconclusive: 'Check the data-quality column in Reports for {target} before the next run.',
    },
    actionKind: 'fix_measurement',
    whereToLook: 'Reports → data quality column; the run bundle’s loss report names the gaps.',
  },
};

/** How confidently the operator may act, per claim strength (AC-1101). */
export const CLAIM_GUIDE: Record<string, string> = {
  causal: 'Strong evidence — safe to act on.',
  directional: 'The direction is reliable but the size is not — act, then verify with an A/B test.',
  diagnostic: 'Weak signal — treat it as a hint, not an instruction.',
  blocked: 'Do not act on this — collect the required evidence first.',
};

const asDirection = (value: string): Direction =>
  value === 'up' || value === 'down' ? value : 'inconclusive';

const fill = (template: string, target: string): string => template.split('{target}').join(target);

/** Name-first target label (AC-1106): enriched name over raw id. */
export const feedTargetLabel = (row: Pick<ProposalFeedRow, 'targetId' | 'targetName'>): string =>
  row.targetName ?? row.targetId;

/**
 * AC-1105/1106: overlay Firestore-resolved names (feedIdentity) onto feed
 * rows. Graph-resolved names (variables) win; actor/audience names come from
 * the audiences collection; an insight's "name" is its canonical description.
 */
export function applyFeedIdentity(
  rows: ProposalFeedRow[],
  identity: ExperimentFeedIdentity | null | undefined,
): ProposalFeedRow[] {
  if (!identity) return rows;
  return rows.map((row) => {
    if (row.targetName) return row;
    if (row.targetEntity === 'actor') {
      const actor = identity.actors[row.targetId];
      return actor?.name ? { ...row, targetName: actor.name } : row;
    }
    if (row.targetEntity === 'audience') {
      const audience = identity.audiences[row.audienceId ?? row.targetId];
      return audience?.name ? { ...row, targetName: audience.name } : row;
    }
    if (row.targetEntity === 'insight') {
      const insight = identity.insights[row.targetId];
      return insight?.description ? { ...row, targetName: insight.description } : row;
    }
    return row;
  });
}

/** "in analysis <name> · <category>" caption for insight-backed targets (AC-1106). */
export function describeAnalysisRef(
  row: Pick<ProposalFeedRow, 'targetEntity' | 'targetId' | 'analysisId'>,
  identity: ExperimentFeedIdentity | null | undefined,
): string | null {
  if (!row.analysisId) return null;
  const name = identity?.analyses[row.analysisId]?.name ?? row.analysisId;
  const category = row.targetEntity === 'insight' ? identity?.insights[row.targetId]?.category : undefined;
  return `in analysis ${name}${category ? ` · ${category}` : ''}`;
}

/* ── Emulation runs (proposal feed → interpretation) ─────────────────────── */

export interface EmulationRunContext {
  surfaceStatus: ExperimentSurfaceStatus;
  trust: ExperimentTrustAssessment;
  feed: ProposalFeedRow[];
  review?: ReviewSummaryBlock | null;
  decision?: { verdict: string; rationale?: string } | null;
}

function feedRowDetail(row: ProposalFeedRow, guide: FamilyGuideEntry): string {
  const target = feedTargetLabel(row);
  if (!row.actionable) {
    const reason = row.blockedReason ? ` (${row.blockedReason})` : '';
    return `${guide.label} — ${target}: blocked by evidence gates${reason}. ${CLAIM_GUIDE.blocked}`;
  }
  const meaning = fill(guide.meaning[asDirection(row.direction)], target);
  return `${guide.label} — ${target}: ${meaning} ${CLAIM_GUIDE[row.claim] ?? CLAIM_GUIDE.diagnostic}`;
}

function feedRowAction(
  row: ProposalFeedRow,
  guide: FamilyGuideEntry,
  reviewDecision?: { decision: string; applyMode?: string },
): InterpretationAction | null {
  const target = feedTargetLabel(row);
  const refs = {
    targetEntity: row.targetEntity,
    targetId: row.targetId,
    ...(row.targetName ? { targetName: row.targetName } : {}),
    ...(row.analysisId ? { analysisId: row.analysisId } : {}),
    ...(row.insightIds?.length ? { insightIds: row.insightIds } : {}),
    ...(row.audienceId ? { audienceId: row.audienceId } : {}),
  };
  // The operator already rejected this proposal — no action item for it.
  if (reviewDecision?.decision === 'rejected') return null;
  if (!row.actionable || reviewDecision?.decision === 'deferred') {
    const evidence = row.nextEvidence ?? row.blockedReason ?? 'more run evidence';
    return {
      kind: 'collect_evidence',
      text: `Collect the missing evidence for ${target}: ${evidence}.`,
      where: guide.whereToLook,
      ...refs,
    };
  }
  const accepted = reviewDecision?.decision === 'accepted'
    ? ` Accepted in review${reviewDecision.applyMode ? ` (${reviewDecision.applyMode.replace(/_/g, ' ')})` : ''} — the run bundle carries the candidate patch.`
    : '';
  return {
    kind: guide.actionKind,
    text: `${fill(guide.action[asDirection(row.direction)], target)}${accepted}`,
    where: guide.whereToLook,
    ...refs,
  };
}

export function interpretEmulationRun(ctx: EmulationRunContext): ExperimentInterpretation {
  const decisionById = new Map(
    (ctx.review?.byId ?? []).map((entry) => [entry.proposalId, { decision: entry.decision, applyMode: entry.applyMode }]),
  );

  const details: string[] = [];
  const actions: InterpretationAction[] = [];
  for (const row of ctx.feed) {
    const guide = FAMILY_GUIDE[row.family];
    if (!guide) {
      // Unknown family (newer compiler than frontend) — degrade readable, never throw.
      details.push(`${row.family.replace(/_/g, ' ')} — ${feedTargetLabel(row)}: ${row.claim} evidence, direction ${row.direction}.`);
      continue;
    }
    details.push(feedRowDetail(row, guide));
    const action = feedRowAction(row, guide, decisionById.get(`${row.family}:${row.targetId}`));
    if (action) actions.push(action);
  }
  // Concrete changes first, evidence collection after (stable within groups).
  actions.sort((a, b) => Number(a.kind === 'collect_evidence') - Number(b.kind === 'collect_evidence'));

  let headline: string;
  const firstChange = actions.find((action) => action.kind !== 'collect_evidence');
  if (ctx.trust.severity === 'critical') {
    headline = `Do not act on this run yet — ${ctx.trust.text}`;
  } else if (ctx.decision) {
    headline = ctx.decision.verdict === 'ship'
      ? `Decided: ship.${ctx.decision.rationale ? ` ${ctx.decision.rationale}` : ''}`
      : `Decided: don't ship.${ctx.decision.rationale ? ` ${ctx.decision.rationale}` : ''}`;
  } else if (firstChange) {
    headline = firstChange.text;
  } else if (ctx.feed.length && actions.length) {
    headline = `All ${ctx.feed.length} proposals are blocked — collect the named evidence before changing the funnel.`;
  } else if (ctx.surfaceStatus === 'ready_to_decide') {
    headline = 'Run completed — review the evidence below and decide.';
  } else {
    headline = 'Still collecting evidence — no proposals for this run yet.';
  }

  return { headline, details, actions };
}

/* ── A/B tests (decision verdict → interpretation) ───────────────────────── */

export interface AbDecisionContext {
  verdict: DecisionVerdictResult;
  /** Raw metric key — drives revenue-first wording (the pricing-test case). */
  metricKey: string;
  /** Human metric label (describeMetric output). */
  metricLabel: string;
  /** Names-first test label (QW1 identity). */
  testLabel: string;
  /** Names-first label of the leading/winning variant, if any. */
  leadingVariantLabel?: string | null;
  /** Registry decision already recorded — switch declare→rollout wording. */
  decided: boolean;
  /** Placement carrying the A/B test (abTestRef) — lets rollout/SRM actions link (AC-C1). */
  placementId?: string | null;
}

const REVENUE_METRICS = new Set(['revenue_per_assigned_user', 'profit_per_assigned_user']);

export function interpretAbDecision(ctx: AbDecisionContext): ExperimentInterpretation {
  const { verdict } = ctx;
  const revenueFirst = REVENUE_METRICS.has(ctx.metricKey);
  const variant = ctx.leadingVariantLabel ?? verdict.leadingVariantId ?? 'the leading variant';
  const probability = verdict.probabilityToBeatControl != null
    ? `${Math.round(verdict.probabilityToBeatControl * 100)}%`
    : null;
  // Placement refs let the card render "open the placement" affordances (AC-C1).
  const placementRefs = ctx.placementId
    ? { targetEntity: 'placement', targetId: ctx.placementId }
    : {};

  if (verdict.deliveryOnly) {
    return {
      headline: `${ctx.testLabel} is serving one effective variant — delivery continues, but this epoch is not an experiment.`,
      details: [
        'Ordinary traffic, conversion, and revenue analytics remain available. SRM and winner decisions start only when at least two variants have positive weight.',
      ],
      actions: [],
    };
  }

  if (verdict.allocationMetadataUnknown) {
    return {
      headline: `Allocation evidence is unavailable on ${ctx.testLabel} — decisions are paused.`,
      details: [verdict.reason],
      actions: [{
        kind: 'fix_measurement',
        text: `Select one immutable allocation epoch for ${ctx.testLabel} or restore its allocation snapshot.`,
        where: 'Reports → decision scorecard; Web placements → A/B publication history.',
        ...placementRefs,
      }],
      stopValve: true,
    };
  }

  if (verdict.sampleRatioMismatch) {
    return {
      headline: `Traffic split is broken on ${ctx.testLabel} — fix the test before trusting any result.`,
      details: [
        'The observed traffic split does not match the configured weights (sample ratio mismatch). Any lift measured under a broken split can be an artifact of who got assigned, not of the variant.',
      ],
      actions: [{
        kind: 'fix_measurement',
        text: `Check variant routing and traffic weights on ${ctx.testLabel}, then restart or re-validate the test.`,
        where: 'Web placements → the A/B test’s variant weights; Reports → decision scorecard.',
        ...placementRefs,
      }],
      stopValve: true,
    };
  }

  if (verdict.verdict === 'dont_ship') {
    return {
      headline: verdict.guardrailBreached
        ? `Don't ship on ${ctx.testLabel} — a guardrail metric regressed.`
        : `Don't ship on ${ctx.testLabel} — ${verdict.reason}`,
      details: [
        verdict.reason,
        ...(verdict.guardrailBreached
          ? ['A win on the primary metric does not cover a loss on a guardrail — shipping would trade one problem for another.']
          : []),
      ],
      actions: [{
        kind: 'collect_evidence',
        text: `Keep control live on ${ctx.testLabel}; investigate the regression in the decision scorecard before re-testing.`,
        where: 'Reports → decision scorecard for this test.',
      }],
    };
  }

  if (verdict.verdict === 'ship') {
    const winSentence = revenueFirst
      ? `it earns more per user${probability ? ` (${probability} chance it beats control on ${ctx.metricLabel})` : ` on ${ctx.metricLabel}`}`
      : probability
        ? `${probability} chance it beats control on ${ctx.metricLabel}`
        : `it beats control on ${ctx.metricLabel}`;
    const details = [verdict.reason];
    if (verdict.expectedLossPerUser != null) {
      details.push(
        revenueFirst
          ? `If this call is wrong, the expected cost is ≈${verdict.expectedLossPerUser.toFixed(2)} per user — small next to the upside.`
          : `Expected loss if this call is wrong: ≈${verdict.expectedLossPerUser.toFixed(2)} per user on ${ctx.metricLabel}.`,
      );
    }
    const actions: InterpretationAction[] = [];
    if (!ctx.decided) {
      actions.push({
        kind: 'declare_winner',
        text: `Declare "${variant}" the winner on ${ctx.testLabel} and record the rationale.`,
        where: 'This card → Declare winner.',
      });
    }
    actions.push({
      kind: 'rollout',
      text: `Roll out "${variant}" to 100% of traffic on ${ctx.testLabel}.`,
      where: 'Web placements → the A/B test’s variant weights.',
      ...placementRefs,
    });
    return {
      headline: `Ship "${variant}" on ${ctx.testLabel} — ${winSentence}.`,
      details,
      actions,
    };
  }

  const details = [
    verdict.reason,
    'More traffic narrows the uncertainty; deciding early on a noisy read costs more than waiting.',
  ];
  if (revenueFirst) {
    details.push('Revenue per user needs more purchases than a conversion-rate test — expect a longer window.');
  }
  return {
    headline: `Keep collecting on ${ctx.testLabel} — ${verdict.reason}`,
    details,
    actions: [{
      kind: 'collect_evidence',
      text: `Let ${ctx.testLabel} keep running; check the decision scorecard when more users have been assigned.`,
      where: 'Reports → decision scorecard for this test.',
    }],
  };
}
